import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "emlak-theme";
const VALID = ["system", "light", "dark"];

const ThemeContext = createContext({
  preference: "system",
  resolved: "light",
  setTheme: () => {},
});

const readCache = () => {
  try {
    const v = localStorage.getItem(CACHE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch (_e) {
    return "system";
  }
};

const writeCache = (v) => {
  try {
    localStorage.setItem(CACHE_KEY, v);
  } catch (_e) {
    /* ignore */
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const resolvePreference = (pref) =>
  pref === "dark" || (pref === "system" && systemPrefersDark())
    ? "dark"
    : "light";

const applyToDom = (resolved) => {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [preference, setPreference] = useState(() => readCache());
  const [resolved, setResolved] = useState(() => resolvePreference(readCache()));
  const syncedFromServer = useRef(false);

  // Apply preference → DOM. Also listen to system theme changes if pref
  // is "system" so a change in OS-level appearance updates the app live.
  useEffect(() => {
    const next = resolvePreference(preference);
    setResolved(next);
    applyToDom(next);
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = resolvePreference("system");
      setResolved(r);
      applyToDom(r);
    };
    // Safari<14 uses addListener/removeListener
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, [preference]);

  // Once the authenticated user's profile is available, the server value
  // wins (source of truth). It supersedes the localStorage cache and
  // re-caches for next hard refresh's pre-hydration script.
  useEffect(() => {
    if (!user || syncedFromServer.current) return;
    const serverPref = user.theme_preference;
    if (VALID.includes(serverPref) && serverPref !== preference) {
      setPreference(serverPref);
      writeCache(serverPref);
    }
    syncedFromServer.current = true;
  }, [user, preference]);

  const setTheme = useCallback(
    async (next) => {
      if (!VALID.includes(next)) return;
      setPreference(next);
      writeCache(next);
      // Best-effort persist to server. Failure is non-fatal — the cache
      // already survives hard refresh; the server value overrides on
      // the next login/refresh only if it exists.
      try {
        await axios.patch(`${API_URL}/api/auth/theme`, { theme: next });
      } catch (_e) {
        /* silent — offline-safe */
      }
    },
    []
  );

  return (
    <ThemeContext.Provider value={{ preference, resolved, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
